'use client';

import { useTranslations } from 'next-intl';
import type { AttachmentTranslationUnsupportedReason } from '@govinity/validation';

import { StatusBadge } from '@govinity/ui/components/ui/status-badge';

import type { TranslatableDocument } from '@/components/ui/translatable-documents';

type DocumentTranslationStatusProps = {
  status: TranslatableDocument['translationStatus'];
  reason?: TranslatableDocument['translationReason'];
  /** Rendered when no translation has been requested — a table cell wants a dash, a header nothing. */
  fallback?: React.ReactNode;
};

// `failed` carries no reason, so it falls back to the cause-agnostic hint.
const UNSUPPORTED_HINT_KEYS = {
  not_pdf: 'originalLanguageHintNotPdf',
  scanned: 'originalLanguageHintScanned',
  too_long: 'originalLanguageHintTooLong',
} as const satisfies Record<AttachmentTranslationUnsupportedReason, string>;

export function DocumentTranslationStatus({
  status,
  reason,
  fallback = null,
}: DocumentTranslationStatusProps) {
  const t = useTranslations('DocumentTranslationStatus');

  switch (status) {
    case 'completed':
      return <StatusBadge variant="success">{t('translated')}</StatusBadge>;

    case 'pending':
      return <StatusBadge variant="info">{t('inProgress')}</StatusBadge>;

    case 'failed':
    case 'unsupported':
      return (
        <StatusBadge
          title={t(reason ? UNSUPPORTED_HINT_KEYS[reason] : 'originalLanguageHint')}
          variant="muted"
        >
          {t('originalLanguage')}
        </StatusBadge>
      );

    default:
      return <>{fallback}</>;
  }
}
